import { computed, Injectable, Signal } from '@angular/core';
import { select } from '@ngxs/store';
import { BusinessServices, IUser, Roles } from '@shs-digi-care-monorepo/interfaces';
import { Stage } from '@shs-digi-care-monorepo/frontend-lib/enums';
import { roleNamesForStream } from '@shs-digi-care-monorepo/frontend-lib/util';
import { UserState } from '@shs-digi-care-monorepo/frontend-lib';
import { environment } from '../../../environments/environment';

/**
 * Role presets
 */
/** Stream-level NAM admin only. */
const ADMIN = [Roles.ADMIN];
/** Roles that work on tasks. */
const TASK_OWNER = [Roles.ADMIN, Roles.TASK_OWNER];

/**
 * All role-gated features in the NAM module.
 * Add new entries here when introducing new role- or stage-specific features.
 */
export enum NamFeature {
    /** Access to the admin area as a whole */
    ADMIN_AREA = 'ADMIN_AREA',
    /** Task list holding every request of the stream */
    ALL_REQUESTS = 'ALL_REQUESTS',
    /** Reagent order tiles linking into the IRO application */
    REAGENT_ORDERS = 'REAGENT_ORDERS',
    /** Allocation request form */
    ALLOCATION = 'ALLOCATION',
    /** Saturday Delivery request type */
    SATURDAY_DELIVERY = 'SATURDAY_DELIVERY',
    /** Material and Pricing request type */
    MATERIAL_AND_PRICING = 'MATERIAL_AND_PRICING',
    /** Profile details tab in the user settings */
    PROFILE_DETAILS = 'PROFILE_DETAILS',
    /** User settings tab in the user settings */
    USER_SETTINGS = 'USER_SETTINGS',
    /** Application settings tab in the user settings */
    APPLICATION_SETTINGS = 'APPLICATION_SETTINGS',
    /** Maintaining one's own absences */
    OUT_OF_OFFICE = 'OUT_OF_OFFICE',
    /** Out-of-office calendar across the teams in scope */
    OUT_OF_OFFICE_CALENDAR = 'OUT_OF_OFFICE_CALENDAR'
}

/**
 * Feature configuration entry defining which roles can access a feature.
 */
interface FeatureConfig {
    /** Roles that have access to this feature. Empty array means all roles have access. */
    roles: Roles[];
    /** Optional: users flagged as Siemens internal get access regardless of their roles. */
    includeInternalUsers?: boolean;
    /** Optional: feature is disabled on these stages */
    disabledOnStages?: Stage[];
}

/**
 * Central feature configuration map.
 * Each entry defines the roles (and optionally the stages) for which a feature is enabled.
 *
 * To add a new role-gated feature:
 * 1. Add an entry to the `NamFeature` enum
 * 2. Add the corresponding config here
 * 3. Use `NamFeatureService.isEnabled(NamFeature.YOUR_FEATURE)` in components
 */
const FEATURE_CONFIG: Record<NamFeature, FeatureConfig> = {
    [NamFeature.ADMIN_AREA]: {
        roles: ADMIN
    },
    [NamFeature.ALL_REQUESTS]: {
        roles: TASK_OWNER
    },
    [NamFeature.REAGENT_ORDERS]: {
        roles: [],
        disabledOnStages: [Stage.PROD]
    },
    [NamFeature.ALLOCATION]: {
        roles: TASK_OWNER,
        includeInternalUsers: true
    },
    [NamFeature.SATURDAY_DELIVERY]: {
        roles: TASK_OWNER,
        includeInternalUsers: true
    },
    [NamFeature.MATERIAL_AND_PRICING]: {
        roles: TASK_OWNER,
        includeInternalUsers: true
    },
    [NamFeature.PROFILE_DETAILS]: {
        roles: TASK_OWNER,
        includeInternalUsers: true
    },
    [NamFeature.USER_SETTINGS]: {
        roles: TASK_OWNER
    },
    [NamFeature.APPLICATION_SETTINGS]: {
        roles: ADMIN
    },
    [NamFeature.OUT_OF_OFFICE]: {
        roles: TASK_OWNER
    },
    [NamFeature.OUT_OF_OFFICE_CALENDAR]: {
        roles: TASK_OWNER
    }
};

/**
 * Service that centralizes role-based feature gating for the NAM module.
 *
 * Instead of scattering `isTaskOwner(user)` checks across components, inject this
 * service and check roles and features via signals. The service reads the current
 * user from the store automatically — no need to pass user around. Roles are scoped
 * to the OM NAM stream, so a role held in another stream does not leak into NAM.
 *
 * @example
 * ```ts
 * // In a component:
 * private readonly features = inject(NamFeatureService);
 *
 * protected isTaskOwner = this.features.isTaskOwner;
 * protected canSeeAllocation = this.features.isEnabled(NamFeature.ALLOCATION);
 * ```
 *
 * @example
 * ```html
 * <!-- In a template: -->
 * @if (canSeeAllocation()) {
 *   <button (click)="requestAllocation()">Request allocation</button>
 * }
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class NamFeatureService {
    private readonly me: Signal<IUser | null> = select(UserState.me);

    /** Role names the current user holds within the OM NAM stream. */
    private readonly namRoles: Signal<Roles[]> = computed(() => roleNamesForStream(this.me(), BusinessServices.OM_NAM));

    /**
     * `true` when the current user administers the OM NAM stream. An admin of
     * another stream (e.g. OM EMEA) must not be granted NAM admin rights.
     */
    public readonly isAdmin: Signal<boolean> = computed(() => this.namRoles().includes(Roles.ADMIN));

    /** `true` when the current user may act as a task owner in the OM NAM stream. */
    public readonly isTaskOwner: Signal<boolean> = computed(() =>
        this.namRoles().some((role) => TASK_OWNER.includes(role))
    );

    /** `true` when the current user is an internal user of the OM NAM stream. */
    public readonly isInternal: Signal<boolean> = computed(() => this.isTaskOwner() || !!this.me()?.isInternal);

    /** `true` when the current user is the assignee of the given request. */
    public isAssignee(request: { assignee?: string | null } | null | undefined): boolean {
        const gid = this.me()?.gid;
        return !!gid && !!request && request.assignee === gid;
    }

    /**
     * Synchronously checks whether the current user holds the given role in the
     * OM NAM stream. Reads the user signal, so calls made inside a `computed`
     * stay reactive.
     */
    public hasRole(role: Roles): boolean {
        return this.namRoles().includes(role);
    }

    /**
     * Returns a computed signal that evaluates to `true` when the given feature
     * is enabled for the current user (based on their role and the current stage).
     */
    public isEnabled(feature: NamFeature): Signal<boolean> {
        return computed(() => this.evaluate(feature));
    }

    /**
     * Synchronously checks whether the given feature is enabled for the current user.
     * Reads the user signal, so calls made inside a `computed`/`effect` stay reactive.
     */
    public isActive(feature: NamFeature): boolean {
        return this.evaluate(feature);
    }

    /**
     * Returns a computed signal that evaluates to `true` when ALL given features
     * are enabled for the current user.
     */
    public allEnabled(...features: NamFeature[]): Signal<boolean> {
        return computed(() => features.every((f) => this.evaluate(f)));
    }

    /**
     * Returns a computed signal that evaluates to `true` when ANY of the given features
     * is enabled for the current user.
     */
    public anyEnabled(...features: NamFeature[]): Signal<boolean> {
        return computed(() => features.some((f) => this.evaluate(f)));
    }

    private evaluate(feature: NamFeature): boolean {
        const user = this.me();
        if (!user) return false;

        const config = FEATURE_CONFIG[feature];

        if (config.disabledOnStages?.includes(environment.stage as Stage)) {
            return false;
        }

        if (config.roles.length === 0) {
            return true;
        }

        const held = this.namRoles();
        return config.roles.some((role) => held.includes(role)) || (!!config.includeInternalUsers && !!user.isInternal);
    }
}
